import { useAuthContext } from 'src/auth/AuthContext';
import Hero from '../components/Hero'
import { Separator, Heading, Flex, VStack, SegmentGroup, HStack, Box, EmptyState } from '@chakra-ui/react';
import CoreOutcomes from 'src/components/CoreOutcomes';
import CustomerFit from 'src/components/CustomerFit';
import Content from 'src/components/Content';
import ProjectTimeline from 'src/components/ProjectTimeline';
import { projectService } from 'src/service/ProjectService';
import { useEffect, useState } from 'react';
import HorizontalTimeline from 'src/components/HorizontalTimeline';
import ExchangeRates from 'src/components/ExchangeRates';
import WaitingSpinner from '@/components/WaitingSpinner';
import Banner from '@/components/Banner';
import { ProjectsTimelineResponse } from '@/api/ProjectsTimelineResponse';
import { LuActivity, LuCalendarClock, LuFolderKanban } from 'react-icons/lu';
import { useI18n } from 'src/i18n/I18nContext';
import { useNavigate } from 'react-router-dom';
import { OrgUtil } from 'src/data/OrgUtil';
import PartnerLanding from './partners/PartnerLanding';
import { User } from '@/api/User';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import HomeWorkspace from './HomeWorkspace';


// logged in landing page
const Dashboard = ({ currentUser }: { currentUser: User }) => {
    const { t } = useI18n();
    const [isLoading, setIsLoading] = useState(true);
    const [projects, setProjects] = useState<ProjectsTimelineResponse | null>(null);
    const [timelineHeight, setTimelineHeight] = useState(700);
    const [timelineError, setTimelineError] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    // future, active, all
    const [timelineRange, setTimelineRange] = useState<string>('future');

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setTimelineError(false);
        projectService
            .listTimelines(timelineRange)
            .then(r => {
                if (cancelled) return;
                setProjects(r);
                // set the height of the project timeline based on the amount of active projects
                if (r.results.length <= 6) {
                    setTimelineHeight(300);
                } else {
                    setTimelineHeight(700);
                }
            })
            .catch(() => { if (!cancelled) setTimelineError(true); })
            .finally(() => { if (!cancelled) setIsLoading(false); });

        return () => { cancelled = true; };
    }, [timelineRange, retryCount]);

    return (
        <Content>
            <Banner relativeTop={-39} />
            <Heading as='h1' size={{ base: '2xl', lg: '3xl' }}>
                {t('landing.welcome', { name: currentUser.firstName })}
            </Heading>
            <HomeWorkspace currentUser={currentUser} />
            <Heading as='h2' size='lg'>{t('landing.timeline.title')}</Heading>
            {timelineError && (
                <Alert status='error' title={t('landing.timeline.loadError')} mt='3'>
                    <Button variant='outline' size='sm' onClick={() => setRetryCount(count => count + 1)}>{t('landing.workspace.retry')}</Button>
                </Alert>
            )}
            <HStack mt='1rem' flexWrap='wrap'>
                <SegmentGroup.Root aria-label={t('landing.timeline.rangeLabel')}
                    value={timelineRange} onValueChange={(e) => setTimelineRange(e.value)}
                >
                    <SegmentGroup.Indicator />
                    <SegmentGroup.Items items={[
                        {
                            value: 'future',
                            label: (<HStack><LuCalendarClock aria-hidden='true' />{t('landing.timeline.activeFuture')}</HStack>),
                        },
                        {
                            value: 'active',
                            label: (<HStack><LuActivity aria-hidden='true' />{t('landing.timeline.activeOnly')}</HStack>),
                        },
                        {
                            value: 'all',
                            label: (<HStack><LuFolderKanban aria-hidden='true' />{t('landing.timeline.all')}</HStack>),
                        },
                    ]} />

                </SegmentGroup.Root>
                {isLoading && (<Box ml='1rem'><WaitingSpinner size='lg' /></Box>)}
            </HStack>
            {projects && (projects.results.length === 0 ? (
                <EmptyState.Root my='8'>
                    <EmptyState.Content>
                        <EmptyState.Indicator><LuFolderKanban /></EmptyState.Indicator>
                        <VStack textAlign='center'>
                            <EmptyState.Title>{t('landing.timeline.emptyTitle')}</EmptyState.Title>
                            <EmptyState.Description>{t('landing.timeline.emptyDescription')}</EmptyState.Description>
                        </VStack>
                    </EmptyState.Content>
                </EmptyState.Root>
            ) : (
                <>
                    <ProjectTimeline
                        projects={projects?.results.concat() ?? []}
                        countries={projects?.countries.concat() ?? []}
                        latestEndDate={projects?.endLatestDate}
                        height={timelineHeight} />
                    <Flex hideBelow='md'>
                        <HorizontalTimeline events={projects?.budgets.concat() ?? []} />
                    </Flex>
                </>
            ))}


            <Separator mt='4rem' mb='1rem' />
            <VStack hideBelow='md'>
                <Heading mb='1rem' textAlign='center'>{t('landing.ratesTitle')}</Heading>
                <ExchangeRates base={"CHF"} all={["USD", "BDT", "KES", "UGX", "NPR"]} />
            </VStack>
        </Content>
    )
}

// logged out landing page
const LoggedOut = () => {
    const { t, language } = useI18n();
    const navigate = useNavigate();

    useEffect(() => {
        document.title = t('hero.metadataTitle');
        document.documentElement.lang = language;
        let description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
        if (!description) {
            description = document.createElement('meta');
            description.name = 'description';
            document.head.appendChild(description);
        }
        description.content = t('hero.metadataDescription');

        const setMeta = (selector: string, attribute: 'name' | 'property', key: string, content: string) => {
            let meta = document.querySelector<HTMLMetaElement>(selector);
            if (!meta) {
                meta = document.createElement('meta');
                meta.setAttribute(attribute, key);
                document.head.appendChild(meta);
            }
            meta.content = content;
        };

        setMeta('meta[property="og:title"]', 'property', 'og:title', t('hero.metadataTitle'));
        setMeta('meta[property="og:description"]', 'property', 'og:description', t('hero.metadataDescription'));
        setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', t('hero.metadataTitle'));
        setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', t('hero.metadataDescription'));
    }, [language, t]);

    return (
        <>
            <Banner relativeTop={-19} />
            <Hero />
            <CoreOutcomes />
            <CustomerFit />
        </>
    );
}

const Landing = () => {
    const { user } = useAuthContext();

    if (!user) return <LoggedOut />;
    if (OrgUtil.isRoleExtern(user.data.role)) return <PartnerLanding user={user.data} />;
    return <Dashboard key={`${user.data.organizationId}.${user.data.id}`} currentUser={user.data} />;
}

export default Landing
